import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { supabase } from "../lib/supabase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  console.log("AuthProvider mounted");

  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);

  const [workspace, setWorkspace] = useState(null);
  const [role, setRole] = useState(null);

  const [loading, setLoading] = useState(true);

  async function loadWorkspace(userId) {
  console.log("loadWorkspace called:", userId);

  if (!userId) {
    setWorkspace(null);
    setRole(null);
    return;
  }

  const { data: memberships, error: membershipError } = await supabase
    .from("workspace_members")
    .select("workspace_id, role, created_at")
    .eq("user_id", userId)
    .order("created_at", { ascending: true })
    .limit(1);

  console.log("Membership result:", { memberships, membershipError });

  if (membershipError) {
    console.error("Failed loading membership:", membershipError);
    setWorkspace(null);
    setRole(null);
    return;
  }

  const membership = Array.isArray(memberships) ? memberships[0] : null;

  if (!membership?.workspace_id) {
    setWorkspace(null);
    setRole(null);
    return;
  }

  const { data: workspaceData, error: workspaceError } = await supabase
    .from("workspaces")
    .select("id, name, owner_id, created_at")
    .eq("id", membership.workspace_id)
    .single();

  console.log("Workspace result:", { workspaceData, workspaceError });

  if (workspaceError) {
    console.error("Failed loading workspace:", workspaceError);
    setWorkspace(null);
    setRole(null);
    return;
  }

  setWorkspace(workspaceData || null);
  setRole(membership.role || null);
}

  useEffect(() => {
    let mounted = true;

    async function initialize() {
      console.log("Auth initialize started");
      setLoading(true);

      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        console.log("Initial session result:", { session, error });

        if (error) throw error;
        if (!mounted) return;

        setSession(session || null);
        setUser(session?.user || null);

        if (session?.user?.id) {
          try {
            await loadWorkspace(session.user.id);
          } catch (workspaceError) {
            console.error("Workspace load crashed:", workspaceError);
            setWorkspace(null);
            setRole(null);
          }
        } else {
          setWorkspace(null);
          setRole(null);
        }
      } catch (error) {
        console.error("Auth initialization failed:", error);

        if (mounted) {
          setSession(null);
          setUser(null);
          setWorkspace(null);
          setRole(null);
        }
      } finally {
        if (mounted) {
          console.log("Auth initialize finished");
          setLoading(false);
        }
      }
    }

    initialize();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
  console.log("Auth state changed:", event, nextSession);

  setSession(nextSession || null);
  setUser(nextSession?.user || null);

  setTimeout(async () => {
    try {
      if (nextSession?.user?.id) {
        await loadWorkspace(nextSession.user.id);
      } else {
        setWorkspace(null);
        setRole(null);
      }
    } catch (error) {
      console.error("Auth state workspace load failed:", error);
      setWorkspace(null);
      setRole(null);
    } finally {
      setLoading(false);
    }
  }, 0);
});

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  async function signOut() {
    await supabase.auth.signOut();
  }

  const value = useMemo(
    () => ({
      session,
      user,
      workspace,
      role,
      loading,
      signOut,
      isAuthenticated: !!session,
      hasWorkspace: !!workspace,
    }),
    [session, user, workspace, role, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return context;
}